/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type AxiosInstance } from 'axios';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import type {
  BrandShelfAcesResponseModel,
  GetBrandListResultsUsingGetParamsModel,
} from '@/api/types/browse-search-types';
import { getAxios } from '@/lib/axios';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import {
  type BaseInfiniteMergeFunction,
  type BaseMergedData,
  createInfiniteQuery,
} from '@/utils/query/createInfiniteQuery';
import { useLocale } from '@/hooks/useLocale';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { type QueryClient } from '@tanstack/react-query';
import { parseUrl } from '@/utils/urlHelpers';
import { showXMPreviewDate } from '@/utils/showXMPreviewDate';
import type { GetProductBrandDataOptionsAces, MappedBrandShelfResultsAces } from '../interface';
import { useMemo } from 'react';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { extractVehicleQuestions } from '@/features/header/utils/getVehicleQuestions';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useMapFacet } from '@/hooks/useMapFacet';
import { useQueryResultOnSuccess } from '@/hooks/query/useQueryResultOnSuccess';
import {
  getExperimentId,
  getSearchExperimentConfig,
  buildSearchExperimentHeaders,
  buildSearchExperimentParams,
} from '@/utils/searchExperiment';
import { validateAndFilterVehicleParams } from '@/features/ymme/utils/validateVehicleParams';
import { useVertexPersonalizationParams } from '@/features/search/hooks/useVertexPersonalizationParams';
import { getSHA256 } from '@/utils/getSHA256';
import { useExperimentId } from '@/utils/searchExperiment/useExperimentId';
import { useRouter } from 'next/navigation';
import environmentConfig from '@/config/environment';
import { useDeviceType } from '@/utils/useDeviceType';
import { getShelfRequestStoreId, getBotEnabledFacetPath } from '@/utils/getShelfRequestStoreId';

const URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-browse-search-data/v2/brand-shelves`;

type OptionsWithoutPage = Omit<GetProductBrandDataOptionsAces, 'pageNumber'>;

const selector = (
  {
    brandShelfResults,
    redirectUrl,
    xFusionQueryId,
    attributionToken,
    filter,
  }: BrandShelfAcesResponseModel,
  mapFacet: ReturnType<typeof useMapFacet>,
): MappedBrandShelfResultsAces => {
  return {
    redirectLocation: redirectUrl,
    recordsPerPage: brandShelfResults?.recordsPerPage ?? 12,
    total: brandShelfResults?.totalNumberOfRecords,
    quickFilters: brandShelfResults?.quickFilters,
    firstRecNum: brandShelfResults?.firstRecordNumber,
    lastRecNum: brandShelfResults?.lastRecordNumber,
    staticNavigation: brandShelfResults?.facets?.map(mapFacet) ?? [],
    brandName: brandShelfResults?.brandName ?? '',
    // Vehicle information from the response
    year: brandShelfResults?.year,
    makeId: brandShelfResults?.makeId,
    modelId: brandShelfResults?.modelId,
    makeName: brandShelfResults?.makeName,
    modelName: brandShelfResults?.modelName,
    subModelName: brandShelfResults?.subModelName,
    subModelId: brandShelfResults?.subModelId,
    unitOfMeasureMatch: brandShelfResults?.unitOfMeasureMatch,
    attributionToken,
    filter,
    records:
      brandShelfResults?.skuRecords?.map((record) => ({
        ...record,
        active: record.activeFlag,
        alternatePartNumber: record.alternatePartNumber,
        brandName: record.brandName,
        brandId: record.brandId,
        seoUrl: record.productDetailsPageUrl,
        imageUrl: record.productImageUrl ?? '',
        productImageUrl: record.productImageUrl ?? '',
        description: record.itemDescription,
        skuNumber: Number(record.itemId),
        locationFilter: record.location,
        lineCode: record.lineCode,
        systemCode: Number(record.originalSystemCode || 0),
        partNumber: record.partNumber,
        productRepositoryId: record.eCommerceProductId,
        productId: record.partTerminologyId,
        quickNote: record.quickNote,
        techNote: record.technicalNote,
        recordType: record.recordType,
        skuReviewEnabled: record.reviewEnabledFlag,
        vehicleFitment: record.vehicleFitment?.vehicleFit,
        vehicleFitmentLabel: record.vehicleFitmentLabel,
        warrantyMonths: record.warranty?.warrantyMonths,
        warrantyText: record.warranty?.warrantyText,
        warrantyType: record.warranty?.warrantyType ?? record.warrantyType,
        brand: record.brandName ?? '',
        name: record.itemDescription,
        productReviewsEnabled: record.reviewEnabledFlag,
        vehicleFit: record.vehicleFitment?.vehicleFit === 'FITS',
        vehicleSpecific: record.vehicleFitmentLabel === 'VEHICLE_SPECIFIC',
        originalPartTypeId: record.originalPartTypeId ?? '',
        // New properties from the ACES API
        subBrandId: record.subBrandId,
        configurableSkuFlag: record.configurableSkuFlag ?? false,
        subBrandName: record.subBrandName,
        partGroupName: record.partGroupName,
        partTerminologyId: record.partTerminologyId,
        partGroupId: record.partGroupId,
        canonicalUrl: record.canonicalUrl,
        categoryIdPath: record.categoryIdPath,
        taxonomyPath: record.taxonomyPath,
        productCanonicalUrl: record.productCanonicalUrl,
        documentId: record.documentId,
        score: record.score,
        perCarQuantity: record.perCarQuantity,
        displayNotesTypes: record.displayNotesTypes,
        availability: record.availability,
        productAttributes: record.productAttributes,
        crossBrand: record.crossBrand,
        crossPartNumber: record.crossPartNumber,
        major: record.major,
        minor: record.minor,
      })) ?? [],
    xFusionQueryId,
  };
};

const getACESBrandProductData = async (
  options: OptionsWithoutPage,
  pageNumber: number,
  axiosInstance?: AxiosInstance,
) => {
  const { locale, experimentId, ...rest } = options;
  const country = getCountryFromLocale(locale);
  const customerType = 'B2C';
  const salesChannel = 'ECOMM';
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { mapFacet: _mapFacet, ...restWithoutMapFacet } = rest;
  const params: GetBrandListResultsUsingGetParamsModel = {
    ...restWithoutMapFacet,
    ignoreVehicleSpecificProductsCheck: true,
    pageNumber,
    country,
    customerType,
    salesChannel,
  };

  const normalizedExperimentId = getExperimentId(experimentId, true);
  const searchExpConfig = getSearchExperimentConfig({
    experimentId: normalizedExperimentId,
    experimentEnabled: true, // Search experiment is always enabled for brand shelves
  });

  // Hash userId if provided (for both client and server-side calls)
  const hashedUserId = options.userId ? await getSHA256(options.userId) : '';
  const enhancedParams = buildSearchExperimentParams(searchExpConfig, params, {
    visitorId: options.visitorId,
    userId: hashedUserId,
  });
  const headers = buildSearchExperimentHeaders(searchExpConfig);

  const response = await getAxios(axiosInstance).get<BrandShelfAcesResponseModel>(URL, {
    ...(headers && { headers }),
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
    params: enhancedParams,
  });

  return response.data;
};

const selectInfiniteQueryData: BaseInfiniteMergeFunction<BrandShelfAcesResponseModel, number> = (
  data,
) => {
  const mergedData = data.pages.reduce<BrandShelfAcesResponseModel>(
    (acc, page) => {
      const { redirectPageType, redirectUrl, xFusionQueryId, attributionToken, filter } = page;
      if (page.brandShelfResults) {
        return {
          ...acc,
          brandShelfResults: {
            ...acc.brandShelfResults,
            ...page.brandShelfResults,
            skuRecords: [
              ...(acc.brandShelfResults?.skuRecords ?? []),
              ...(page.brandShelfResults.skuRecords ?? []),
            ],
          },
          redirectPageType,
          redirectUrl,
          xFusionQueryId,
          attributionToken,
          filter,
        };
      }
      return acc;
    },
    {
      brandShelfResults: { skuRecords: [] },
    } as unknown as BrandShelfAcesResponseModel,
  );

  return {
    mergedData,
    pages: data.pages,
    pageParams: data.pageParams,
    pageNumber: data.pages.length,
  };
};

const {
  useData: useACESBrandProducts,
  prefetch: prefetchACESProductBrand,
  query: acesProductBrandQuery,
  getFromCache,
} = createInfiniteQuery<
  BrandShelfAcesResponseModel,
  OptionsWithoutPage,
  number,
  BaseMergedData<BrandShelfAcesResponseModel, number>
>(['acesBrandProducts'], getACESBrandProductData, {
  initialPageParam: 1,
  getNextPageParam: (lastPage, allPages) => {
    const totalRecords = lastPage.brandShelfResults?.totalNumberOfRecords ?? 0;
    const currentPageNumber = allPages.length;

    // Count actual records loaded across all pages
    const loadedRecords = allPages.reduce((sum, page) => {
      return sum + (page.brandShelfResults?.skuRecords?.length ?? 0);
    }, 0);

    // Only fetch next page if we haven't loaded all records yet
    if (loadedRecords < totalRecords) {
      return currentPageNumber + 1;
    }
    return undefined;
  },
  selectInfiniteQueryData,
});

export const useACESBrandProductData = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const locale = useLocale();
  const isBot = useDeviceType() === 'bot';
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const { acesEnabled } = useAcesFlags();
  const experimentId = useExperimentId();
  const preferredVehicle = usePreferredVehicle();
  const { data: storeDetailsData } = useStoreDetailsData();
  const storeNumber = storeDetailsData?.storeNumber;
  const vehicleId = preferredVehicle?.catalogVehicleId;
  const { sort = [], facet, minPrice, maxPrice } = legacyRouter.query;
  const sortArr = typeof sort === 'string' ? sort.split('-') : sort;
  const { data: headerData } = useHeaderData();
  const sortOrder = sortArr[sortArr.length - 1];
  const sortFieldName = sortArr.slice(0, sortArr.length - 1).join('-');
  const vehicleQuestions = useMemo(
    () =>
      extractVehicleQuestions(
        headerData?.vehicleMap?.acesVehicleAttributes?.vehicleQuestionsIds,
        true,
      ) as string,
    [headerData?.vehicleMap],
  );
  const mapFacet = useMapFacet();
  const path = legacyRouter.asPath;
  const parsedUrl = parseUrl(path);
  const botEnabledFacetPath = getBotEnabledFacetPath(parsedUrl.url);
  const vertexParams = useVertexPersonalizationParams();

  // Validate vehicle parameters - if any base param is missing, exclude ALL vehicle params
  const validatedVehicleParams = validateAndFilterVehicleParams({
    year: preferredVehicle?.year,
    makeId: preferredVehicle?.makeId,
    modelId: preferredVehicle?.modelId,
    vehicleTypeId: preferredVehicle?.vehicleTypeId,
    vehicleQuestions: vehicleQuestions || undefined,
  });

  const queryOptions: OptionsWithoutPage = {
    locale,
    vehicleId,
    recordsPerPage: 24,
    minPrice: minPrice ? String(minPrice) : undefined,
    maxPrice: maxPrice ? String(maxPrice) : undefined,
    ...validatedVehicleParams,
    facet: Array.isArray(facet) ? facet.join(',') : facet,
    mapFacet: mapFacet,
    sort: sortFieldName && sortOrder ? `${sortFieldName}-${sortOrder}` : undefined,
    storeId: getShelfRequestStoreId(storeNumber, isBot, botEnabledFacetPath),
    seoUrl: parsedUrl.url,
    preview: showXMPreviewDate(),
    ...(experimentId && { experimentId }),
    ...(vertexParams.shouldInjectParams && {
      visitorId: vertexParams.visitorId,
      userId: vertexParams.userId,
    }),
  };

  const infiniteQueryResult = useACESBrandProducts({
    queryOptions,
    enabled: enabled && vertexParams.isMyProfileReady && acesEnabled,
  });

  const {
    data: infiniteData,
    status,
    refetch,
    isError,
    isSuccess,
    isPending,
    isFetching,
    fetchStatus,
    hasNextPage,
    fetchNextPage,
    hasPreviousPage,
    fetchPreviousPage,
    isFetchingNextPage,
  } = infiniteQueryResult;

  useQueryResultOnSuccess(
    infiniteQueryResult,
    (data) => {
      const lastPage = data.pages[data.pages.length - 1];
      if (lastPage.redirectUrl) {
        router.replace(lastPage.redirectUrl);
      }
    },
    true,
  );

  const { mergedData, pages, pageNumber = 1 } = infiniteData ?? {};

  const selectedData = selector(
    mergedData ??
      ({ brandShelfResults: { skuRecords: [] } } as unknown as BrandShelfAcesResponseModel),
    mapFacet,
  );

  return {
    pages,
    data: selectedData,
    pageNumber,
    fetchNextPage,
    hasNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    isFetching,
    isPending,
    status,
    refetch,
    fetchStatus,
    isSuccess,
    isError,
    isFetchingNextPage,
  };
};

export const prefetchACESBrandInfinityData = (
  queryClient: QueryClient,
  options: GetProductBrandDataOptionsAces,
  axiosInstance: AxiosInstance,
) => {
  return prefetchACESProductBrand(queryClient, options, axiosInstance);
};

export const getACESBrandProductDataFromCache = (
  queryClient: QueryClient,
  options: GetProductBrandDataOptionsAces,
) => {
  return getFromCache(queryClient, options);
};

export { acesProductBrandQuery };
